import { motion } from 'framer-motion';
import { 
  Car, Shield, Users, Calendar,
  ChevronRight
} from 'lucide-react';

const features = [
  {
    icon: Car,
    title: "Smart Fleet Tracking",
    description: "Real-time GPS tracking and analytics for your entire luxury fleet",
    benefits: ["Live location updates", "Route optimization", "Fuel efficiency monitoring"]
  },
  {
    icon: Shield,
    title: "Advanced Security",
    description: "Enterprise-grade security features to protect your valuable assets",
    benefits: ["24/7 monitoring", "Anti-theft systems", "Insurance integration"]
  },
  {
    icon: Users,
    title: "Staff Management",
    description: "Comprehensive tools for managing drivers and support staff",
    benefits: ["Performance tracking", "Schedule optimization", "Training modules"]
  },
  {
    icon: Calendar,
    title: "Smart Booking System",
    description: "Streamlined reservation management for your luxury fleet",
    benefits: ["Automated scheduling", "Client preferences", "Dynamic pricing"]
  }
];

export default function FeatureShowcase() {
  return (
    <section className="py-24 bg-black">
      <div className="max-w-[90%] 2xl:max-w-[1800px] mx-auto px-4 sm:px-6">
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="heading-jaguar mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-200 to-amber-400">
              Premium Features
            </span>
          </h2>
          <p className="body-jaguar text-gray-400 text-sm max-w-2xl mx-auto">
            Experience the future of luxury fleet management with our cutting-edge features
          </p>
        </motion.div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8 mx-auto">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group p-10 rounded-2xl bg-zinc-900/30 border border-zinc-800 
                         transition-all duration-500 hover:bg-zinc-800/50 hover:border-amber-500/30"
              >
                <div className="flex items-start gap-6">
                  <div className="p-3 rounded-lg bg-amber-400/10 transition-colors duration-500 
                               group-hover:bg-amber-400/20">
                    <Icon className="w-7 h-7 text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-jaguar text-lg font-light text-white mb-3 
                                transition-colors duration-500 group-hover:text-amber-200">
                      {feature.title}
                    </h3>
                    <p className="body-jaguar text-gray-400 text-sm mb-6 
                               transition-colors duration-500 group-hover:text-gray-300">
                      {feature.description}
                    </p>
                    <div className="space-y-3">
                      {feature.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs text-gray-500 
                                              transition-all duration-500 group-hover:translate-x-1">
                          <ChevronRight className="w-3.5 h-3.5 text-amber-400 
                                               transition-colors duration-500 group-hover:text-amber-300" />
                          <span className="font-light tracking-wide 
                                       transition-colors duration-500 group-hover:text-gray-400">
                            {benefit}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
} 